import { useEffect, useMemo, useState, type ReactNode } from "react";
import { placeAds } from "@/lib/ads";
import { stopAnalytics } from "@/lib/analytics";
import {
    keepVisitorChoice,
    privacySignal,
    readVisitorChoice,
    storageOf,
    visitorAnalyticsOn,
    type VisitorChoice,
} from "@/lib/consent";
import { useAsksFirst } from "@/lib/hooks/analyticsRegion";
import AnalyticsBanner from "./AnalyticsBanner";
import { analyticsAskable, AnalyticsChoiceContext, turnAnalyticsOn } from "./analyticsChoice";

/**
 * A visitor's analytics choice, kept in this browser. In the European
 * Economic Area, the United Kingdom and Switzerland, and wherever the
 * place cannot be read, nothing runs until they say yes in the banner.
 * Elsewhere analytics is on until they say no, from the footer's link,
 * and session replay waits for a yes there too.
 * Global Privacy Control is a no: such a visitor is never asked, and
 * analytics never starts. A browser that will not keep the choice is
 * asked again on the next visit. Until the sign-in and the place are
 * read, nothing is asked and nothing starts. The ad partners
 * follow analytics, and only where nothing asks first (lib/ads).
 */
export default function VisitorConsent({
    ready,
    children,
}: {
    ready: boolean;
    children: ReactNode;
}) {
    const [gpc] = useState(() => privacySignal());
    const [choice, setChoice] = useState<VisitorChoice>(() =>
        readVisitorChoice(storageOf("localStorage")),
    );
    const [reopened, setReopened] = useState(false);
    const askFirst = useAsksFirst(ready && !gpc);
    const placed = ready && askFirst !== undefined;

    useEffect(() => {
        if (!ready) return;
        if (askFirst !== undefined && visitorAnalyticsOn(choice, askFirst, gpc))
            turnAnalyticsOn("record", null, choice === "granted");
        else stopAnalytics("record");
    }, [ready, choice, askFirst, gpc]);

    useEffect(() => placeAds(ready && askFirst === false && !gpc), [ready, askFirst, gpc]);

    const context = useMemo(
        () => (placed && !gpc ? { open: () => setReopened(true) } : null),
        [placed, gpc],
    );

    const answer = (granted: boolean) => {
        keepVisitorChoice(storageOf("localStorage"), granted);
        setReopened(false);
        setChoice(granted ? "granted" : "refused");
        if (granted) turnAnalyticsOn("choice", null);
        else stopAnalytics("choice");
    };

    const asking =
        placed && !gpc && analyticsAskable() && ((choice === "unasked" && askFirst) || reopened);
    return (
        <AnalyticsChoiceContext.Provider value={context}>
            {children}
            {asking && (
                <AnalyticsBanner
                    byDefault={!askFirst}
                    onAllow={() => answer(true)}
                    onDecline={() => answer(false)}
                />
            )}
        </AnalyticsChoiceContext.Provider>
    );
}
